import { useRef } from "react";
import type { FilmProject, StoryboardFrame, UploadedAssetManifest, UploadedAssetRecord } from "../types";
import { frameSourceClass, frameSourceLabel, frameStatus } from "../utils/storyboard";
import { Icon } from "./Icon";

type AssetBucket = keyof UploadedAssetManifest;

interface AssetLibraryDrawerProps {
  project: FilmProject;
  activeSceneId?: string;
  selectedFrameId?: string;
  onClose: () => void;
  onUseFrame?: (frame: StoryboardFrame) => void;
  onAttachReference?: (assetId: string) => void;
  onToggleAssetDisabled: (bucket: AssetBucket, assetId: string) => void;
  onDeleteAsset: (bucket: AssetBucket, assetId: string) => void;
  onRenameAsset: (bucket: AssetBucket, assetId: string) => void;
  onDuplicateAsset: (bucket: AssetBucket, assetId: string) => void;
  onMoveAsset: (bucket: AssetBucket, assetId: string, target: AssetBucket) => void;
  onUploadAssets: (bucket: AssetBucket, files: File[], context?: Partial<UploadedAssetRecord>) => void;
  onRetryAssetUpload: (bucket: AssetBucket, assetId: string) => void;
}

const bucketLabels: Record<AssetBucket, string> = {
  characterCards: "Character Cards",
  sceneReferences: "Scene References",
  voiceAudio: "Voice Audio",
  music: "Music",
  sfx: "SFX",
  videoReferences: "Video References",
  styleRefs: "Style / AI Refs",
  branding: "Branding & Props",
  documents: "Documents"
};

const bucketUpload: Record<AssetBucket, { accept: string; kind: string }> = {
  characterCards: { accept: "image/*,.pdf", kind: "character_card" },
  sceneReferences: { accept: "image/*,.pdf", kind: "scene_reference" },
  voiceAudio: { accept: "audio/*,.m4a,.mp3,.wav,.aiff", kind: "voice_audio" },
  music: { accept: "audio/*,.m4a,.mp3,.wav,.aiff", kind: "music" },
  sfx: { accept: "audio/*,.pdf,.txt", kind: "sfx" },
  videoReferences: { accept: "video/*,.mp4,.mov,.webm", kind: "video_reference" },
  styleRefs: { accept: "image/*,.png,.jpg,.jpeg,.webp", kind: "style_reference" },
  branding: { accept: "image/*,.pdf", kind: "branding" },
  documents: { accept: ".pdf,.docx,.txt,.md", kind: "document" }
};

function assets(project: FilmProject): UploadedAssetManifest {
  return project.uploadedAssets || {
    characterCards: [],
    sceneReferences: [],
    voiceAudio: [],
    music: [],
    sfx: [],
    videoReferences: [],
    styleRefs: [],
    branding: [],
    documents: []
  };
}

function assetSourceLabel(asset: UploadedAssetRecord): string {
  if (asset.sourceMode === "seed_generated" || asset.provider === "evolink" || asset.provider === "seedance" || asset.provider === "seed") return "EvoLink Seedance";
  if (asset.provider === "openai") return "OpenAI";
  return "User upload";
}

function assetStatus(asset: UploadedAssetRecord): string {
  if (asset.disabled) return "Disabled";
  if (asset.cloudStatus === "pending") return "Upload pending";
  if (asset.cloudStatus === "failed") return "Upload failed";
  if (asset.cloudStatus === "local_only") return "Local only";
  if (asset.cloudStatus === "uploaded") return "Uploaded";
  if (asset.locked) return "Locked";
  if (asset.parseStatus === "parsed") return "Parsed";
  return "Available";
}

function assetStatusClass(asset: UploadedAssetRecord): string {
  if (asset.disabled) return "disabled";
  if (!asset.cloudStatus) return "";
  return `cloud-${String(asset.cloudStatus).replace(/[^a-z0-9_-]+/gi, "-").toLowerCase()}`;
}

function frameSceneLabel(project: FilmProject, frame: StoryboardFrame): string {
  const scene = project.scenes.find((item) => item.id === frame.sceneId);
  return scene ? `Scene ${String(scene.number).padStart(2, "0")} · ${scene.slugline}` : frame.sceneId;
}

export function AssetLibraryDrawer({
  project,
  activeSceneId,
  selectedFrameId,
  onClose,
  onUseFrame,
  onAttachReference,
  onToggleAssetDisabled,
  onDeleteAsset,
  onRenameAsset,
  onDuplicateAsset,
  onMoveAsset,
  onUploadAssets,
  onRetryAssetUpload
}: AssetLibraryDrawerProps) {
  const manifest = assets(project);
  const activeScene = project.scenes.find((scene) => scene.id === activeSceneId);
  const boardFrames = project.scenes.flatMap((scene) => scene.frames).filter((frame) => frame.id !== selectedFrameId);
  const buckets = Object.keys(bucketLabels) as AssetBucket[];
  const attachedReferenceIds = new Set(
    manifest.sceneReferences
      .filter((asset) => asset.sceneId === activeSceneId)
      .map((asset) => asset.assetId)
      .filter((assetId): assetId is string => Boolean(assetId))
  );

  return (
    <div className="drawer-backdrop" role="presentation">
      <aside className="asset-library-drawer" role="dialog" aria-modal="true" aria-label="Asset library">
        <header>
          <div>
            <span>Asset Library</span>
            <h2>Reuse source material</h2>
            <p>Uploads and locked boards stay source-of-truth. Reuse them across scenes without regenerating.</p>
          </div>
          <button className="icon-button" onClick={onClose} type="button" aria-label="Close asset library">
            <Icon name="chevron" />
          </button>
        </header>

        <section className="asset-library-section">
          <div className="asset-section-heading">
            <span>Storyboard Boards</span>
            <strong>{boardFrames.length}</strong>
          </div>
          <div className="library-board-grid">
            {boardFrames.slice(0, 18).map((frame) => (
              <article key={frame.id}>
                <img src={frame.imageUrl} alt="" />
                <div>
                  <span className="library-board-tags">
                    <em>{frameStatus(frame)}</em>
                    <b className={`library-source ${frameSourceClass(frame)}`}>{frameSourceLabel(frame)}</b>
                  </span>
                  <strong>{frame.shotType}</strong>
                  <small>{frameSceneLabel(project, frame)}</small>
                </div>
                {onUseFrame && selectedFrameId ? (
                  <button className="secondary-action" onClick={() => onUseFrame(frame)} type="button">Use on selected frame</button>
                ) : null}
              </article>
            ))}
          </div>
        </section>

        {buckets.map((bucket) => (
          <section className="asset-library-section" key={bucket}>
            <div className="asset-section-heading">
              <span>{bucketLabels[bucket]}</span>
              <strong>{manifest[bucket].length}</strong>
              <LibraryUploadButton
                accept={bucketUpload[bucket].accept}
                label="Upload"
                onFiles={(files) => onUploadAssets(bucket, files, { kind: bucketUpload[bucket].kind })}
              />
            </div>
            {!manifest[bucket].length ? (
              <p className="asset-bucket-empty">Nothing here yet — upload {bucketLabels[bucket].toLowerCase()} above.</p>
            ) : null}
            <div className="library-asset-list">
              {manifest[bucket].map((asset) => (
                <article className={[asset.disabled ? "disabled" : "", assetStatusClass(asset)].filter(Boolean).join(" ")} key={asset.assetId || `${bucket}-${asset.fileName}`}>
                  <div>
                    <span>{assetSourceLabel(asset)} · {assetStatus(asset)}</span>
                    <strong>{asset.label}</strong>
                    <small>{asset.fileName}{asset.sceneId ? ` · scene ${asset.sceneId}` : ""}</small>
                  </div>
                  <div className="asset-row-actions">
                    {bucket === "sceneReferences" && asset.assetId && onAttachReference && activeSceneId ? (
                      <button
                        className="secondary-action"
                        disabled={attachedReferenceIds.has(asset.assetId)}
                        onClick={() => onAttachReference(asset.assetId!)}
                        type="button"
                      >
                        {attachedReferenceIds.has(asset.assetId) ? "Attached" : "Attach to scene"}
                      </button>
                    ) : null}
                    {asset.assetId ? (
                      <button className="secondary-action" onClick={() => onToggleAssetDisabled(bucket, asset.assetId!)} type="button">
                        {asset.disabled ? "Enable" : "Disable"}
                      </button>
                    ) : null}
                    {asset.assetId ? (
                      <button className="secondary-action" onClick={() => onRenameAsset(bucket, asset.assetId!)} type="button">
                        Rename
                      </button>
                    ) : null}
                    {asset.assetId ? (
                      <button className="secondary-action" onClick={() => onDuplicateAsset(bucket, asset.assetId!)} type="button">
                        Duplicate
                      </button>
                    ) : null}
                    {asset.storageKey ? (
                      <a
                        className="secondary-action"
                        download={asset.fileName}
                        href={`/api/assets/object/${String(asset.storageKey).split("/").map(encodeURIComponent).join("/")}`}
                      >
                        Download
                      </a>
                    ) : null}
                    <button
                      className="secondary-action"
                      onClick={() => { void navigator.clipboard?.writeText(asset.fileName); }}
                      title="Copy the file name — paste it into a beat's refs to cast this asset"
                      type="button"
                    >
                      Copy name
                    </button>
                    {asset.assetId ? (
                      <select
                        className="secondary-action asset-move"
                        onChange={(event) => {
                          const target = event.target.value as AssetBucket;
                          event.target.value = "";
                          if (target) onMoveAsset(bucket, asset.assetId!, target);
                        }}
                        title="Move to another category"
                        value=""
                      >
                        <option value="">Move to…</option>
                        {(Object.keys(bucketLabels) as AssetBucket[]).filter((b) => b !== bucket).map((b) => (
                          <option key={b} value={b}>{bucketLabels[b]}</option>
                        ))}
                      </select>
                    ) : null}
                    {asset.assetId ? (
                      <button className="secondary-action danger" onClick={() => onDeleteAsset(bucket, asset.assetId!)} type="button">
                        Delete
                      </button>
                    ) : null}
                    {asset.assetId && asset.cloudStatus === "failed" ? (
                      <button className="secondary-action" onClick={() => onRetryAssetUpload(bucket, asset.assetId!)} type="button">
                        Retry upload
                      </button>
                    ) : null}
                  </div>
                </article>
              ))}
            </div>
          </section>
        ))}
      </aside>
    </div>
  );
}

function LibraryUploadButton({ accept, label, onFiles }: { accept: string; label: string; onFiles: (files: File[]) => void }) {
  const inputRef = useRef<HTMLInputElement>(null);
  return (
    <>
      <input
        ref={inputRef}
        className="visually-hidden"
        type="file"
        multiple
        accept={accept}
        onChange={(event) => {
          const files = Array.from(event.target.files || []);
          if (files.length) onFiles(files);
          event.target.value = "";
        }}
      />
      <button className="secondary-action" onClick={() => inputRef.current?.click()} type="button">
        <Icon name="upload" />
        {label}
      </button>
    </>
  );
}
